/// <amd-module name="Artifacts/Package/containers/VersionsTabContainer" />
import { PackageVersionsTable } from "Artifacts/Package/components/PackageVersionsTable";
import { getPackageDetailsCommands } from "Artifacts/Package/helpers/PackageDetailsHelper";
import { usePackageDetails } from "Artifacts/Package/hooks/PackageDetailsResolver";
import { IPackageDetailsModuleState } from "Artifacts/Package/redux";
import { ICommandControlProps } from "Artifacts/Package/types";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { ComponentState } from "Artifacts/Root/Constants";
import { encodeName, encodeVersion } from "Artifacts/Root/helpers/PackageDecoderHelper";
import { useFeed } from "Artifacts/Root/hooks/FeedUrlResolver";
import { ProtocolProvider } from "Artifacts/Root/protocols/components/ProtocolProvider";
import { useProtocol } from "Artifacts/Root/protocols/hooks/Protocol";
import { packageDetailsOverview, packageDetailsVersions } from "Artifacts/Root/routing/Constants";
import { IPackage, IPackageVersion, IPackageVersionIdentity } from "Artifacts/Root/types";
import { SkeletalPackageVersion } from "Artifacts/Root/types/AzureArtifacts";
import * as PackageResources from "Feed/Common/Resources";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { generatePath } from "react-router-dom";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { useComponentContext } from "VSS/Platform/Layout";
import { IListSelection, ListSelection } from "VSSUI/List";

export interface IVersionsTabContainerMapToStateProps {
  pkg?: IPackage;
  versions?: SkeletalPackageVersion[];
  selection: IListSelection;
  currentVersion?: IPackageVersion;
  componentState: ComponentState;
}

const mapStateToProps = (
  state: IPackageDetailsModuleState,
): IVersionsTabContainerMapToStateProps => {
  const pkg = state.packageState.data.pkg;

  return {
    versions: state.packageState.data.packageVersionsList,
    selection: state.packageState.ui.packageListSelection,
    pkg: pkg,
    currentVersion: state.packageState.data.currentPackageVersion,
    componentState: state.packageState.ui.packageDetailsComponentState,
  };
};

const VersionsTabContainer: React.FC<IVersionsTabContainerMapToStateProps &
  ICommandControlProps> = ({ versions, setCommands, pkg, currentVersion, componentState }) => {
  const { pageContext } = useComponentContext();
  const protocol = useProtocol(pkg);
  const [selection] = useState<IListSelection>(
    () => new ListSelection({ selectOnFocus: false, multiSelect: true }),
  );
  const { feed } = useFeed();

  const onSelect = useCallback((): void => {
    if (pkg && versions) {
      const selectedVersions: IPackageVersionIdentity[] = [];
      for (const s of selection.value) {
        selectedVersions.push(
          ...versions.slice(s.beginIndex, s.endIndex + 1).map<IPackageVersionIdentity>((v) => ({
            name: pkg.name,
            packageId: pkg.id,
            version: v.version,
            normalizedVersion: v.normalizedVersion,
            protocol: pkg.protocol,
            views: v.views,
            isListed: v.isListed,
            isDeleted: v.isDeleted,
          })),
        );
      }

      protocol &&
        setCommands(getPackageDetailsCommands(pageContext, protocol, selectedVersions, feed, pkg));
    }
  }, [setCommands, selection, pageContext, protocol, versions, pkg, feed]);

  useEffect(() => {
    setCommands([]);
    selection.subscribe(onSelect);
    return () => {
      selection.unsubscribe(onSelect);
    };
  }, [onSelect, selection, setCommands]);

  function onClick(version: string): string {
    if (pkg) {
      return generatePath(packageDetailsOverview, {
        feedIdentifier: feed?.name,
        packageName: encodeName(pkg.name, pkg.protocol),
        protocol: pkg.protocol,
        version: encodeVersion(version, pkg.protocol),
      });
    }
    return "";
  }

  usePackageDetails(packageDetailsVersions);

  return (
    <DefaultLoader state={componentState} primaryText={PackageResources.FilterList_NoResults}>
      {feed && pkg && (
        <ProtocolProvider pkg={pkg}>
          {(protocol) => (
            <DynamicModuleLoader modules={[protocol.getReduxModule()]}>
              <PackageVersionsTable
                feed={feed}
                pkg={pkg}
                onClick={(version: string) => onClick(version)}
                versions={versions}
                selection={selection}
                currentVersion={currentVersion}
                componentState={componentState}
              />
            </DynamicModuleLoader>
          )}
        </ProtocolProvider>
      )}
    </DefaultLoader>
  );
};

const ConnectedVersionsTabContainer = connect<
  IVersionsTabContainerMapToStateProps,
  unknown,
  ICommandControlProps
>(
  mapStateToProps,
  null,
)(VersionsTabContainer);

export { ConnectedVersionsTabContainer as VersionsTabContainer };
